import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { LogIn, LogOut, XCircle, Users, ShieldCheck, Camera, Clock, ArrowLeft } from "lucide-react";
import QrScanner from "../components/QrScanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return "-"; }
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
}

export default function Gate() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const now = useClock();

  const [lastResult, setLastResult] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [insideCount, setInsideCount] = useState(0);
  const [processing, setProcessing] = useState(false);
  const lastScanRef = useRef({ token: "", at: 0 });

  const allowedRole = user && ["admin", "shift_leader", "quality"].includes(user.role);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  const refreshAttendance = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/gate/attendance`);
      setInsideCount(r.data.inside_count || 0);
      const evs = (r.data.events || []).slice(-8).reverse();
      setRecentEvents(evs);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { if (allowedRole) refreshAttendance(); }, [allowedRole, refreshAttendance]);
  useEffect(() => {
    if (!allowedRole) return;
    const t = setInterval(refreshAttendance, 15000);
    return () => clearInterval(t);
  }, [allowedRole, refreshAttendance]);

  const handleScan = useCallback(async (decodedText) => {
    if (!decodedText) return;
    const nowMs = Date.now();
    // Anti-double-scan: skip if same token within 4s
    if (lastScanRef.current.token === decodedText && nowMs - lastScanRef.current.at < 4000) return;
    lastScanRef.current = { token: decodedText, at: nowMs };
    setProcessing(true);
    try {
      const r = await axios.post(`${API}/gate/scan`, { token: decodedText.trim() });
      const data = r.data;
      setLastResult({ ...data, receivedAt: Date.now() });
      // Play beep
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = data.action === "check_in" ? 880 : 660;
          gain.gain.value = 0.15;
          osc.connect(gain); gain.connect(ctx.destination);
          osc.start(); osc.stop(ctx.currentTime + 0.18);
        }
      } catch { /* ignore */ }
      // Vibrate
      if (navigator.vibrate) navigator.vibrate(120);
      refreshAttendance();
    } catch (e) {
      const msg = e?.response?.data?.detail || "QR desconhecido";
      setLastResult({ ok: false, reason: msg, receivedAt: Date.now() });
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  }, [refreshAttendance]);

  // Scanner handled by <QrScanner /> component below.

  if (!allowedRole) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
      <div className="text-center">
        <XCircle className="h-16 w-16 mx-auto text-red-500" />
        <h1 className="text-2xl font-bold mt-4">Acesso negado</h1>
        <p className="text-slate-300 mt-2">O modo Portaria requer perfil Administrador ou Chefe de Turno.</p>
        <Button className="mt-6" onClick={() => navigate("/")} data-testid="gate-back">Voltar</Button>
      </div>
    </div>;
  }

  const cardBg = !lastResult
    ? "bg-slate-800"
    : !lastResult.ok
    ? "bg-red-900"
    : lastResult.action === "check_in"
    ? "bg-emerald-900"
    : "bg-amber-800";

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col" data-testid="gate-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-800 bg-slate-900">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-orange-500" />
          <div>
            <div className="text-xl font-bold tracking-wide">PORTARIA EP3D</div>
            <div className="text-xs text-slate-400">Modo Quiosque • Ligado por {user.name || user.user_code}</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-3xl font-mono font-bold" data-testid="gate-clock">
              {now.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
            <div className="text-xs text-slate-400">{now.toLocaleDateString("pt-PT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div>
          </div>
          <div className="flex flex-col items-center bg-slate-800 rounded-lg px-4 py-2">
            <Users className="h-5 w-5 text-emerald-400" />
            <div className="text-2xl font-bold text-emerald-400" data-testid="gate-inside">{insideCount}</div>
            <div className="text-[10px] text-slate-400 uppercase">na fábrica</div>
          </div>
          <Button variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-800" onClick={() => navigate("/")} data-testid="gate-exit">
            <ArrowLeft className="h-4 w-4 mr-1" /> Sair
          </Button>
        </div>
      </div>

      {/* Main content grid */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[380px_1fr_320px] gap-6 p-6">
        {/* LEFT: camera */}
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2 mb-3 text-slate-300">
            <Camera className="h-5 w-5" />
            <span className="text-sm uppercase tracking-wide">Leitor QR</span>
          </div>
          <QrScanner
            onDecode={handleScan}
            size={340}
            borderColor="#f97316"
            scannerId="gate-reader"
            testId="gate-reader"
          />
          {processing && (
            <div className="mt-3 text-orange-300 text-sm animate-pulse">A validar QR...</div>
          )}
          <p className="mt-4 text-xs text-slate-400 text-center max-w-xs">
            Aponte o crachá (operador ou visitante) para o leitor. Uma leitura regista entrada; outra na mesma pessoa regista saída.
          </p>
        </div>

        {/* CENTER: last result big display */}
        <div className={`rounded-2xl p-8 flex flex-col items-center justify-center transition-colors ${cardBg}`} data-testid="gate-result">
          {!lastResult ? (
            <div className="text-center opacity-60">
              <Camera className="h-24 w-24 mx-auto text-slate-500" />
              <div className="text-2xl font-semibold mt-4 text-slate-400">Aguardando leitura...</div>
              <div className="text-sm text-slate-500 mt-2">Aproxime o crachá do leitor.</div>
            </div>
          ) : !lastResult.ok ? (
            <div className="text-center">
              <XCircle className="h-32 w-32 mx-auto text-red-300" />
              <div className="text-4xl font-black mt-4">ACESSO NEGADO</div>
              <div className="text-xl mt-2">{lastResult.reason || "QR inválido"}</div>
              {lastResult.name && <div className="text-lg text-red-200 mt-2">{lastResult.name}</div>}
            </div>
          ) : (
            <div className="text-center">
              {lastResult.action === "check_in" ? (
                <LogIn className="h-24 w-24 mx-auto text-emerald-300" />
              ) : (
                <LogOut className="h-24 w-24 mx-auto text-amber-200" />
              )}
              <div className="text-5xl font-black mt-3 uppercase">
                {lastResult.action === "check_in" ? "Entrada" : "Saída"}
              </div>
              <div className="mt-6 flex items-center gap-6 justify-center">
                {lastResult.photo_url ? (
                  <img src={lastResult.photo_url} alt=""
                       className="w-40 h-40 rounded-xl object-cover border-4 border-white/40"
                       crossOrigin="anonymous" />
                ) : (
                  <div className="w-40 h-40 rounded-xl bg-white/10 border-4 border-white/40 flex items-center justify-center text-6xl font-black">
                    {(lastResult.name || "?").charAt(0)}
                  </div>
                )}
                <div className="text-left">
                  <div className="text-4xl font-bold" data-testid="result-name">{lastResult.name}</div>
                  {lastResult.type === "operator" ? (
                    <>
                      <div className="text-lg text-white/80 mt-1">{lastResult.function || "-"}</div>
                      <div className="text-sm text-white/60">{lastResult.user_code} · {lastResult.department || "-"}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-lg text-white/80 mt-1">Visitante · {lastResult.company || "sem empresa"}</div>
                      <div className="text-sm text-white/60">{lastResult.purpose}</div>
                    </>
                  )}
                  <div className="text-xs text-white/50 mt-3 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {fmtTime(lastResult.timestamp)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: recent events */}
        <div className="bg-slate-900 rounded-2xl p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm uppercase tracking-wide text-slate-400">Últimos registos</div>
            <Users className="h-4 w-4 text-slate-500" />
          </div>
          <div className="space-y-2 overflow-y-auto flex-1" data-testid="gate-recent">
            {recentEvents.length === 0 && (
              <div className="text-sm text-slate-500 text-center pt-6">Sem eventos hoje ainda</div>
            )}
            {recentEvents.map(ev => (
              <div key={ev.id} className={`p-2 rounded-lg flex items-center gap-2 text-sm ${ev.action === "check_in" ? "bg-emerald-950" : "bg-amber-950"}`}>
                {ev.action === "check_in"
                  ? <LogIn className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                  : <LogOut className="h-4 w-4 text-amber-400 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{ev.subject_name}</div>
                  <div className="text-xs text-slate-400">
                    {ev.subject_type === "operator" ? "Operador" : "Visitante"} · {fmtTime(ev.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
